import { createFileRoute, Link } from "@tanstack/react-router";
import { getArticles, getCategories } from "@/lib/sanity.server";
import BlogCard from "@/components/blog/BlogCard";
import Pagination from "@/components/blog/Pagination";
import CTA from "@/components/LandingPage/CTA";
import { createLLMOMeta, createBreadcrumbSchema } from "@/lib/seo";

const parsePositivePage = (value: unknown) => {
  const page = typeof value === "number" ? value : typeof value === "string" ? Number(value) : 1;
  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
};

export const Route = createFileRoute("/blog_/category/$slug")({
  validateSearch: (search) => ({
    page: parsePositivePage(search.page),
  }),
  loaderDeps: ({ search: { page } }) => ({ page }),
  loader: async ({ params, deps: { page } }) => {
    const [{ articles, pagination }, categories] = await Promise.all([
      getArticles({ data: { page, pageSize: 9, category: params.slug } }),
      getCategories(),
    ]);

    const category = categories.find((c) => c.slug === params.slug);

    return {
      articles,
      pagination,
      category,
      categorySlug: params.slug,
      categoryName: category?.name || params.slug.replace(/-/g, " "),
    };
  },
  head: ({ loaderData }) => {
    const name = loaderData?.categoryName || "Category";
    const title = `${name} - Blog | Zapify`;
    const url = `https://zapify.pro/blog/category/${loaderData?.categorySlug}`;

    return {
      meta: [
        ...createLLMOMeta({
          title,
          description: `Browse all ${name} articles on Zapify Blog.`,
          url,
        }),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        createBreadcrumbSchema({
          itemListElement: [
            { position: 1, name: "Home", item: "https://zapify.pro" },
            { position: 2, name: "Blog", item: "https://zapify.pro/blog" },
            { position: 3, name: name, item: url },
          ],
        }),
      ],
    };
  },
  component: CategoryPage,
});

function CategoryPage() {
  const { articles, pagination, categoryName, categorySlug } = Route.useLoaderData();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-16">
        {/* Breadcrumbs */}
        <nav className="mb-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
          <span>/</span>
          <span className="text-foreground capitalize">{categoryName}</span>
        </nav>

        {/* Category Header */}
        <div className="mb-12">
          <div className="mb-4">
            <span className="inline-block rounded-full px-4 py-2 text-sm font-medium bg-primary-gradient text-primary-foreground">
              {categoryName}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-foreground mb-4 capitalize">
            {categoryName}
          </h1>
          <p className="text-muted-foreground">
            {pagination.total} {pagination.total === 1 ? "post" : "posts"}
          </p>
        </div>

        {/* Posts Grid */}
        {articles.length > 0 ? (
          <>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {articles.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
            <div className="mt-12">
              <Pagination
                currentPage={pagination.page}
                pageCount={pagination.pageCount}
                pageSize={pagination.pageSize}
                total={pagination.total}
              />
            </div>
          </>
        ) : (
          <div className="py-16 text-center">
            <p className="text-lg text-muted-foreground">No posts in this category yet.</p>
            <Link
              to="/blog"
              className="mt-4 inline-flex text-primary hover:underline font-medium"
            >
              ← Back to Blog
            </Link>
          </div>
        )}
      </div>
      <CTA />
    </div>
  );
}
